import React, { useCallback } from 'react'
import { Button } from '@demandlocal/components'
import { useModal } from '../../components/useModal'
import { motion } from 'framer-motion'
import { toast } from 'react-hot-toast'
import { useGetOrganizationByOrganizationId } from '../../api/admin/useGetAgencyByAgencyId'
import { useDeleteAgencyById } from '../../api/admin/useDeleteAgency'
import { queryClient } from '../../api/queryClient'

type Props = {
  agency: any
}

const AdminDeleteAgency = ({ agency }: Props): JSX.Element => {
  const { closeModal } = useModal()
  const { data: agencyData, isLoading: isLoadingAgency } =
    useGetOrganizationByOrganizationId(agency?.id)
  const { mutateAsync: deleteAgency, isLoading } = useDeleteAgencyById()

  const onDeleteAudiencePress = useCallback(
    async (e) => {
      e.preventDefault()
      await deleteAgency({ agency_id: agency.id }).then(() => {
        closeModal()
        queryClient.refetchQueries(['admin', 'organizations'], { active: true })
        toast.success('Agency successfully deleted!')
      })
    },
    [agency.id, closeModal, deleteAgency],
  )

  return (
    <div className={'flex flex-1 flex-col px-12 pt-12 font-Spartan h-full'}>
      <div className={'flex flex-1 flex-col overflow-hidden'}>
        <div className={'font-bold text-blue-primary text-[28px]'}>
          Are you sure you want to delete this agency?
        </div>
        <div className={'flex-1 flex items-center'}>
          <div className={'flex flex-1 flex-col gap-5'}>
            <div className={'pb-5 border-b-2 flex-1 flex flex-col'}>
              <span className={'text-blue-primary font-bold text-sm'}>Agency name</span>
              <span className={'mt-3 font-medium text-base'}>{agency.name}</span>
            </div>
            {!isLoadingAgency && (agencyData?.assigned_dealerships?.length ?? 0) > 0 && (
              <div className={'pb-5 flex-1 flex flex-col'}>
                <span className={'text-blue-primary font-bold text-sm'}>
                  Assigned Dealerships
                </span>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: 'spring', mass: 1, damping: 50, stiffness: 500 }}
                  className={'overflow-hidden'}>
                  <div
                    className={
                      'mt-3 flex flex-row flex-1 flex-wrap justify-start items-end gap-3'
                    }>
                    {agencyData?.assigned_dealerships?.map(
                      (value: any, index: number) => {
                        return (
                          <motion.div
                            key={`${index}`}
                            className={'flex flex-row p-2 rounded-lg bg-gainsboro-gray'}>
                            <div
                              className={
                                'font-bold flex flex-col justify-between text-xs'
                              }>
                              <span>{value.vendor_account_vendor_account_name}</span>
                              <span className={'font-medium text-xs'}>
                                {value.dealership}
                              </span>
                            </div>
                          </motion.div>
                        )
                      },
                    )}
                  </div>
                </motion.div>
              </div>
            )}
            {!isLoadingAgency && (agencyData?.assigned_users?.length ?? 0) > 0 && (
              <div className={'pb-5 flex-1 flex flex-col'}>
                <span className={'text-blue-primary font-bold text-sm'}>
                  Assigned Users
                </span>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: 'spring', mass: 1, damping: 50, stiffness: 500 }}
                  className={'overflow-hidden'}>
                  <div
                    className={
                      'mt-3 flex flex-row flex-1 flex-wrap justify-start items-end gap-3'
                    }>
                    {agencyData?.assigned_users?.map((value: any, index: number) => {
                      return (
                        <motion.div
                          key={`${index}`}
                          className={'flex flex-row p-2 rounded-lg bg-gainsboro-gray'}>
                          <div
                            className={'font-bold flex flex-col justify-between text-xs'}>
                            <span>{value.vendor_account_vendor_account_name}</span>
                            <span className={'font-medium text-xs'}>
                              {`${value?.first_name} ${value?.last_name ?? ''}`}
                            </span>
                          </div>
                        </motion.div>
                      )
                    })}
                  </div>
                </motion.div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={'h-20 items-center flex justify-between border-t-2 py-4 mt-4'}>
        <Button
          variant={'secondary'}
          onClick={(e) => {
            e.preventDefault()
            closeModal()
          }}>
          Close
        </Button>
        <Button disabled={isLoading} type={'submit'} onClick={onDeleteAudiencePress}>
          Delete agency
        </Button>
      </div>
    </div>
  )
}

export default AdminDeleteAgency
